import React, { useMemo, useState } from "react";
import { Box, Chip, InputAdornment, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { NEUTRAL, ORANGE, RADIUS, SELECTED_EDGE, VIOLET } from "../../styles/brandTokens";
import { INTEGRATION_CATEGORIES, IntegrationCategoryId } from "../../utils/connectorUtils";
import { DirectoryEntry } from "./integrationsModel";

interface IntegrationsDirectoryProps {
  entries: DirectoryEntry[];
  /** Card click: start a new connection for this entry. */
  onConnect: (entry: DirectoryEntry) => void;
  /** "N connected" chip click: jump to the Active tab for this entry's connections. */
  onShowConnected: (entry: DirectoryEntry) => void;
}

/**
 * The catalogue of everything Theo can connect to, browsed by category.
 * Connected entries stay listed (badged) rather than disappearing, so the
 * Directory always answers "what can this product talk to".
 */
const IntegrationsDirectory: React.FC<IntegrationsDirectoryProps> = ({
  entries,
  onConnect,
  onShowConnected,
}) => {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<IntegrationCategoryId | "all">("all");

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    entries.forEach((entry) => map.set(entry.category, (map.get(entry.category) || 0) + 1));
    return map;
  }, [entries]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return entries.filter((entry) => {
      if (category !== "all" && entry.category !== category) return false;
      if (!needle) return true;
      return (
        entry.name.toLowerCase().includes(needle) ||
        entry.description.toLowerCase().includes(needle)
      );
    });
  }, [entries, search, category]);

  const rail: Array<{ id: IntegrationCategoryId | "all"; label: string; count: number }> = [
    { id: "all", label: "All integrations", count: entries.length },
    ...INTEGRATION_CATEGORIES.filter((entry) => (counts.get(entry.id) || 0) > 0).map((entry) => ({
      id: entry.id as IntegrationCategoryId | "all",
      label: entry.title,
      count: counts.get(entry.id) || 0,
    })),
  ];

  return (
    <Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start", flexWrap: { xs: "wrap", md: "nowrap" } }}>
      {/* Category rail: the directory is browsed, so the taxonomy stays visible. */}
      <Box
        sx={{
          flex: "0 0 auto",
          width: { xs: "100%", md: 220 },
          border: `1px solid ${NEUTRAL.border}`,
          borderRadius: `${RADIUS.card}px`,
          bgcolor: NEUTRAL.surface,
          overflow: "hidden",
        }}
      >
        {rail.map((item) => {
          const selected = category === item.id;
          return (
            <Box
              key={item.id}
              role="button"
              tabIndex={0}
              onClick={() => setCategory(item.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setCategory(item.id);
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                px: 1.75,
                py: 1.1,
                cursor: "pointer",
                bgcolor: selected ? NEUTRAL.hover : "transparent",
                boxShadow: selected ? SELECTED_EDGE : "none",
                "&:hover": { bgcolor: NEUTRAL.hover },
                "&:focus-visible": { outline: `2px solid ${VIOLET.main}`, outlineOffset: -2 },
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: selected ? 700 : 500,
                  color: selected ? NEUTRAL.text : NEUTRAL.textSecondary,
                }}
              >
                {item.label}
              </Typography>
              <Typography sx={{ fontSize: 12, color: NEUTRAL.textMuted, fontVariantNumeric: "tabular-nums" }}>
                {item.count}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ flex: "1 1 auto", minWidth: 0, width: { xs: "100%", md: "auto" } }}>
        <TextField
          size="small"
          placeholder="Search integrations…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ mb: 2, width: "100%", maxWidth: 420 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: NEUTRAL.textMuted }} />
              </InputAdornment>
            ),
          }}
        />

        {visible.length === 0 ? (
          <Box
            sx={{
              border: `1px solid ${NEUTRAL.border}`,
              borderRadius: `${RADIUS.card}px`,
              p: 4,
              textAlign: "center",
              bgcolor: NEUTRAL.surface,
            }}
          >
            <Typography sx={{ fontWeight: 600, color: NEUTRAL.text }}>No integrations match</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
            }}
          >
            {visible.map((entry) => {
              const connectable = !entry.comingSoon;
              return (
              <Box
                key={entry.key}
                role={connectable ? "button" : undefined}
                tabIndex={connectable ? 0 : undefined}
                aria-disabled={connectable ? undefined : true}
                onClick={connectable ? () => onConnect(entry) : undefined}
                onKeyDown={
                  connectable
                    ? (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onConnect(entry);
                        }
                      }
                    : undefined
                }
                sx={{
                  position: "relative",
                  border: `1px solid ${NEUTRAL.border}`,
                  borderRadius: `${RADIUS.card}px`,
                  bgcolor: connectable ? NEUTRAL.surface : NEUTRAL.page,
                  p: 2,
                  cursor: connectable ? "pointer" : "default",
                  transition: "border-color 120ms ease",
                  ...(connectable
                    ? {
                        "&:hover": { borderColor: NEUTRAL.borderStrong },
                        "&:focus-visible": { outline: `2px solid ${VIOLET.main}`, outlineOffset: -2 },
                      }
                    : {}),
                }}
              >
                {entry.comingSoon ? (
                  <Chip
                    size="small"
                    label="Coming soon"
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      height: 20,
                      fontSize: 11,
                      bgcolor: NEUTRAL.hover,
                      color: NEUTRAL.textSecondary,
                    }}
                  />
                ) : entry.connectedCount > 0 ? (
                  <Chip
                    size="small"
                    label={`${entry.connectedCount} connected`}
                    title="View in Active"
                    aria-label={`${entry.connectedCount} connected, view in Active`}
                    // The chip sits inside the card's button; stop the event so a
                    // chip click navigates to Active instead of starting a new connection.
                    onClick={(event) => {
                      event.stopPropagation();
                      onShowConnected(entry);
                    }}
                    onKeyDown={(event) => event.stopPropagation()}
                    sx={{
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#D2ECDB" },
                      "&.MuiChip-clickable:hover": { bgcolor: "#D2ECDB" },
                      position: "absolute",
                      top: 12,
                      right: 12,
                      height: 20,
                      fontSize: 11,
                      bgcolor: "#E6F4EA",
                      color: "#1B6B38",
                    }}
                  />
                ) : null}
                <Box
                  component="img"
                  src={entry.logo}
                  alt=""
                  sx={{ width: 32, height: 32, objectFit: "contain", mb: 1.25, display: "block" }}
                />
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: NEUTRAL.text, mb: 0.25 }}>
                  {entry.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: NEUTRAL.textSecondary,
                    mb: 1.5,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {entry.description}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: connectable ? ORANGE.text : NEUTRAL.textMuted,
                    letterSpacing: "0.03em",
                  }}
                >
                  {connectable ? (entry.scope === "msp" ? "MSP LEVEL" : "PER CUSTOMER") : "NOT YET AVAILABLE"}
                </Typography>
              </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default IntegrationsDirectory;
